import { useLayoutEffect, type RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const TITLE_SEL = '[data-reveal="title"]';
const IMAGE_SEL = '[data-reveal="image"]';

/**
 * Scoped, play-once editorial reveals.
 * - Titles: rise 24px out of a rectangular clip.
 * - Images: rectangular mask opens bottom→top while the photo settles from 1.05.
 * Initial states are set before paint (no flash). Reduced motion: nothing is hidden or animated.
 */
export default function useEditorialReveal(scope: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const root = scope.current;
    if (!root) return undefined;
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.utils.toArray<HTMLElement>(root.querySelectorAll(TITLE_SEL)).forEach((el) => {
        gsap.fromTo(
          el,
          { y: 24, clipPath: 'inset(0% 0% 100% 0%)' },
          {
            y: 0,
            clipPath: 'inset(0% 0% -20% 0%)',
            duration: 0.8,
            ease: 'power3.out',
            clearProps: 'transform,clipPath',
            scrollTrigger: { trigger: el, start: 'top 88%', once: true },
          },
        );
      });
      gsap.utils.toArray<HTMLElement>(root.querySelectorAll(IMAGE_SEL)).forEach((el) => {
        const img = el.querySelector('img');
        const tl = gsap.timeline({
          scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        });
        tl.fromTo(
          el,
          { clipPath: 'inset(100% 0% 0% 0%)' },
          { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.9, ease: 'power3.out', clearProps: 'clipPath' },
          0,
        );
        if (img) {
          tl.fromTo(img, { scale: 1.05 }, { scale: 1, duration: 1, ease: 'power3.out', clearProps: 'transform' }, 0);
        }
      });
      return undefined;
    });
    return () => mm.revert();
  }, [scope]);
}